import React, { useEffect, useMemo, useState } from "react";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { getAuthToken } from "./api";
import { EVENT_TYPE_LABELS } from "./services/jobService";
import "./CompanyDashboard.css";

/* ── Icons ──────────────────────────────────────────────── */
const IJob      = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="14" rx="2"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M3 12h18"/></svg>;
const ICand     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M3 21a9 9 0 0118 0"/></svg>;
const IStar     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z"/></svg>;
const IWarn     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.86L1.82 18A2 2 0 003.24 21h17.52a2 2 0 001.74-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>;
const IArrow    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
const IBack     = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>;
const IReport   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h6"/></svg>;
const IPlay     = () => <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>;
const IPlus     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>;
const IDownload = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>;
const IFilter   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 6h18M7 12h10M10 18h4"/></svg>;
const ISort     = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M7 12h10M10 18h4"/></svg>;

/* ── Helpers ─────────────────────────────────────────────── */
function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return s; }
}

function scoreClass(v) {
  if (v >= 80) return "good";
  if (v >= 60) return "warn";
  return "bad";
}

/* ── KPI card ────────────────────────────────────────────── */
function KpiCard({ label, value, sub, icon, accent }) {
  return (
    <div className={`cd-kpi${accent ? " cd-kpi--accent" : ""}`}>
      <div className="cd-kpi-top">
        <span className="cd-kpi-label">{label}</span>
        <span className={`cd-kpi-icon${accent ? " cd-kpi-icon--accent" : ""}`}>{icon}</span>
      </div>
      <div className="cd-kpi-value">{value}</div>
      {sub && <div className="cd-kpi-sub">{sub}</div>}
    </div>
  );
}

/* ── Score bar ───────────────────────────────────────────── */
function ScoreBar({ value }) {
  const cls = scoreClass(value);
  return (
    <div className="cd-score-bar">
      <div className="cd-score-track">
        <span className={`cd-score-fill ${cls}`} style={{ width: `${value}%` }} />
      </div>
      <span className={`cd-score-val ${cls}`}>{value}</span>
    </div>
  );
}

/* ── Integrity ring ──────────────────────────────────────── */
function IntegrityRing({ score, level, flags }) {
  const r = 13, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const col = level === "high" ? "#36d399" : level === "medium" ? "#f59e0b" : "#f87171";
  return (
    <div className="cd-integrity">
      <div className="cd-integrity-ring">
        <svg viewBox="0 0 32 32" width="36" height="36">
          <circle cx="16" cy="16" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
          <circle cx="16" cy="16" r={r} fill="none" stroke={col} strokeWidth="3"
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            transform="rotate(-90 16 16)" />
        </svg>
        <div className="cd-integrity-pct">{score}</div>
      </div>
      <div>
        <div className={`cd-integrity-lvl ${level}`}>{level}</div>
        {flags?.length > 0
          ? <details className="cd-flags"><summary>{flags.length} flag{flags.length > 1 ? "s" : ""}</summary><ul>{flags.map((f, i) => <li key={i}>{f}</li>)}</ul></details>
          : <span className="cd-flags-clean">Clean</span>}
      </div>
    </div>
  );
}

/* ── Rank badge ──────────────────────────────────────────── */
function RankBadge({ rank }) {
  const cls = rank === 1 ? "gold" : rank === 2 ? "silver" : rank === 3 ? "bronze" : "";
  return <span className={`cd-rank ${cls}`}>#{rank}</span>;
}

/* ── Status pill ─────────────────────────────────────────── */
function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const label = { active: "Active", expired: "Expired", draft: "Draft", review: "In review" }[s] || status;
  return <span className={`cd-status-pill cd-status-${s}`}>{label}</span>;
}

/* ── Event-type badge ────────────────────────────────────── */
function EventTypeBadge({ eventType, includeCoding }) {
  const type = eventType || "INTERVIEW";
  const label = type === "MCQ" && includeCoding ? "MCQ + Coding" : (EVENT_TYPE_LABELS[type] || type);
  return <span className={`cd-event-badge ${type.toLowerCase()}`}>{label}</span>;
}

/* ── Pipeline bar row ────────────────────────────────────── */
function PipelineRow({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="cd-pipe-row">
      <div className="cd-pipe-label">
        <span className="cd-pipe-dot" style={{ background: color }} />
        {label}
      </div>
      <span className="cd-pipe-num">{value}</span>
      <div className="cd-pipe-bar">
        <span style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

/* ── Scatter tooltip ─────────────────────────────────────── */
function ScatterTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <div className="cd-tooltip">
      <div className="cd-tooltip-ref">{d.name}</div>
      <div>Technical: <strong>{d.technical}</strong></div>
      <div>Overall: <strong>{d.overall}</strong></div>
      <div>Rank: <strong>#{d.rank}</strong></div>
    </div>
  );
}

/* ── MCQ / Coding results page (shared layout, different columns) ─────── */
function AssessmentResultsPage({ job_name, event_type, results, total, onBack }) {
  const isMcq = event_type === "MCQ";
  const hasCoding = results.some((r) => r.coding_total != null);
  const avgScore = isMcq && total > 0
    ? Math.round(results.reduce((s, r) => s + (r.scores?.total || 0), 0) / total)
    : null;
  const highIntegrityPct = total > 0
    ? Math.round(results.filter((r) => r.integrity_level === "high").length / total * 100)
    : 0;

  return (
    <div className="cd-page">
      <div className="cd-results-head">
        <div className="cd-results-left">
          <button className="cd-back-btn" onClick={onBack}><IBack /> Back to Jobs</button>
          <h1 className="cd-results-title">{job_name} <EventTypeBadge eventType={event_type} includeCoding={hasCoding} /></h1>
          <div className="cd-results-meta">
            <span className="cd-tag"><ICand /> {total} candidates</span>
            {isMcq && avgScore !== null && <span className="cd-tag">{avgScore}% avg score</span>}
          </div>
        </div>
      </div>

      {total === 0 ? (
        <div className="cd-empty">No candidates have completed this assessment yet.</div>
      ) : (
        <>
          <div className="cd-kpi-row">
            <KpiCard label="Candidates" value={total} icon={<ICand />} />
            {isMcq
              ? <KpiCard label="Avg. Score" value={`${avgScore}%`} sub="out of 100" icon={<IStar />} />
              : <KpiCard
                  label="Avg. Solved"
                  value={Math.round(results.reduce((s, r) => s + (r.solved || 0), 0) / total * 10) / 10}
                  sub={`out of ${results[0]?.total ?? "—"} problems`}
                  icon={<IStar />}
                />}
            <KpiCard label="High Integrity" value={`${highIntegrityPct}%`} sub={`${results.filter(r => r.integrity_level !== "high").length} flagged`} icon={<IWarn />} accent />
          </div>

          <div className="cd-panel" style={{ marginTop: "1rem" }}>
            <div className="cd-panel-head">
              <div>
                <h2>Candidate results</h2>
                <p className="cd-panel-sub">Sorted by most recent submission</p>
              </div>
            </div>
            <div className="cd-table-scroll">
              <table className="cd-cand-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    {isMcq ? <><th>Score</th><th>Correct</th><th>Time used</th></> : <th>Problems solved</th>}
                    {hasCoding && <th>Coding</th>}
                    <th>Integrity</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="cd-cand-name">
                          <div className="cd-cand-avatar">{(r.name || r.email || "??").slice(-2).toUpperCase()}</div>
                          <span>{r.name || r.email || "Candidate"}</span>
                        </div>
                      </td>
                      {isMcq ? (
                        <>
                          <td><span className={`cd-overall-cell ${scoreClass(r.scores?.total ?? 0)}`}>{r.scores?.total ?? "—"}</span></td>
                          <td>{r.correct_answers ?? 0}/{r.total_questions ?? 0}</td>
                          <td>{r.time_used_ms ? `${Math.round(r.time_used_ms / 60000)} min` : "—"}</td>
                        </>
                      ) : (
                        <td>{r.solved ?? 0}/{r.total ?? 0}</td>
                      )}
                      {hasCoding && <td>{r.coding_total != null ? `${r.coding_solved ?? 0}/${r.coding_total}` : "—"}</td>}
                      <td><IntegrityRing score={r.integrity_score ?? 100} level={r.integrity_level ?? "high"} flags={[]} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════ */
function CompanyDashboard({ user }) {
  const [jobs, setJobs] = useState([]);
  const [selectedJobData, setSelectedJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobLoading, setJobLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => { fetchJobs(); }, []);

  async function fetchJobs() {
    setLoading(true); setError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication required");
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/dashboard/company`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      setJobs(data.jobs || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function fetchJobResults(jobId) {
    setJobLoading(true); setError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication required");
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/dashboard/company/job/${jobId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      setSelectedJobData(data);
    } catch (e) { setError(e.message); }
    finally { setJobLoading(false); }
  }

  function handleBack() { setSelectedJobData(null); }

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="cd-page">
        <div className="cd-loading">
          <div className="cd-spinner" />
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error && !selectedJobData) {
    return (
      <div className="cd-page">
        <div className="cd-error-card">
          <h3>Unable to load dashboard</h3>
          <p>{error}</p>
          <button className="cd-btn cd-btn-primary" onClick={fetchJobs}>Retry</button>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════
     JOB RESULTS VIEW
     ══════════════════════════════════════ */
  if (selectedJobData) {
    const eventType = selectedJobData.event_type || "INTERVIEW";

    if (eventType === "MCQ" || eventType === "CODING") {
      return (
        <AssessmentResultsPage
          job_name={selectedJobData.job_name}
          event_type={eventType}
          results={selectedJobData.results || []}
          total={selectedJobData.total || 0}
          onBack={handleBack}
        />
      );
    }

    const { job_name, candidates = [], total_candidates = 0 } = selectedJobData;

    const avg = (key) => total_candidates > 0
      ? (candidates.reduce((s, c) => s + (c.scores?.[key] || 0), 0) / total_candidates).toFixed(1)
      : "—";

    const integrityPct = total_candidates > 0
      ? Math.round(candidates.filter(c => c.integrity?.level === "high").length / total_candidates * 100)
      : 0;

    const scatterData = candidates.map(c => ({
      name: c.candidate_ref,
      technical: c.scores?.technical ?? 0,
      overall: c.scores?.overall ?? 0,
      rank: c.rank,
    }));

    const skillAvgs = [
      { label: "Technical",      val: Math.round(+avg("technical"))      },
      { label: "Communication",  val: Math.round(+avg("communication"))  },
      { label: "Confidence",     val: Math.round(+avg("confidence"))     },
      { label: "Overall",        val: Math.round(+avg("overall"))        },
    ];

    return (
      <div className="cd-page">
        {/* ── Results header ── */}
        <div className="cd-results-head">
          <div className="cd-results-left">
            <button className="cd-back-btn" onClick={handleBack}>
              <IBack /> Back to Jobs
            </button>
            <h1 className="cd-results-title">{job_name}</h1>
            <div className="cd-results-meta">
              <span className="cd-tag"><ICand /> {total_candidates} candidates</span>
              <span className="cd-tag">{avg("overall")} avg score</span>
            </div>
          </div>
          <div className="cd-results-actions">
            <button className="cd-btn cd-btn-ghost"><IDownload /> Export CSV</button>
            <button className="cd-btn cd-btn-primary">Shortlist top 5</button>
          </div>
        </div>

        {/* ── Mini KPIs ── */}
        {total_candidates === 0 ? (
          <div className="cd-empty">No candidates have completed interviews for this job yet.</div>
        ) : (
          <>
            <div className="cd-kpi-row">
              <KpiCard label="Candidates"     value={total_candidates} sub={`${candidates.filter(c => c.scores?.overall).length} completed`} icon={<ICand />} />
              <KpiCard label="Avg. Technical" value={avg("technical")} sub="out of 100" icon={<IStar />} />
              <KpiCard label="Avg. Overall"   value={avg("overall")}   sub="composite score" icon={<IStar />} />
              <KpiCard label="High Integrity" value={`${integrityPct}%`} sub={`${candidates.filter(c => c.integrity?.level !== "high").length} flagged`} icon={<IWarn />} accent />
            </div>

            {/* ── Split: scatter + skill breakdown ── */}
            <div className="cd-split cd-split-3-2">
              {/* Scatter */}
              <div className="cd-panel">
                <div className="cd-panel-head">
                  <div>
                    <h2>Score distribution</h2>
                    <p className="cd-panel-sub">Technical vs. Overall — each dot is one candidate</p>
                  </div>
                </div>
                <div className="cd-scatter-wrap">
                  <ResponsiveContainer width="100%" height={280}>
                    <ScatterChart margin={{ top: 16, right: 20, bottom: 24, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                      <XAxis dataKey="technical" type="number" domain={[0, 100]}
                        tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
                        label={{ value: "Technical →", position: "insideBottomRight", offset: -4, fill: "rgba(255,255,255,0.45)", fontSize: 11 }} />
                      <YAxis dataKey="overall" type="number" domain={[0, 100]}
                        tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
                        label={{ value: "Overall →", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.45)", fontSize: 11 }} />
                      <Tooltip content={<ScatterTip />} cursor={{ stroke: "rgba(255,255,255,0.15)" }} />
                      <Scatter data={scatterData} fill="#764ba2"
                        shape={(props) => {
                          const { cx, cy, payload } = props;
                          const top = payload.technical >= 75 && payload.overall >= 75;
                          const r = payload.rank <= 3 ? 7 : 5;
                          return (
                            <g>
                              <circle cx={cx} cy={cy} r={r + 3} fill={top ? "#f59e0b" : "#764ba2"} fillOpacity={0.18} />
                              <circle cx={cx} cy={cy} r={r} fill={top ? "#f59e0b" : "#667eea"} stroke="rgba(255,255,255,0.6)" strokeWidth={1.5} />
                            </g>
                          );
                        }}
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Skill breakdown */}
              <div className="cd-panel">
                <div className="cd-panel-head">
                  <div>
                    <h2>Skill breakdown</h2>
                    <p className="cd-panel-sub">Cohort averages</p>
                  </div>
                </div>
                <div className="cd-pipeline">
                  {skillAvgs.map(({ label, val }) => (
                    <PipelineRow key={label} label={label} value={val} max={100}
                      color={val >= 75 ? "#36d399" : val >= 60 ? "#667eea" : "#f59e0b"} />
                  ))}
                </div>
              </div>
            </div>

            {/* ── Candidates table ── */}
            <div className="cd-panel" style={{ marginTop: "1rem" }}>
              <div className="cd-panel-head">
                <div>
                  <h2>Ranked candidates</h2>
                  <p className="cd-panel-sub">Sorted by overall score · pseudonymized refs</p>
                </div>
                <div className="cd-panel-tools">
                  <button className="cd-btn cd-btn-ghost cd-btn-sm"><ISort /> Sort</button>
                  <button className="cd-btn cd-btn-ghost cd-btn-sm"><IDownload /> Export</button>
                </div>
              </div>
              <div className="cd-table-scroll">
                <table className="cd-cand-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Candidate</th>
                      <th>Technical</th>
                      <th>Communication</th>
                      <th>Confidence</th>
                      <th>Overall</th>
                      <th>Integrity</th>
                      <th>Report</th>
                      <th>Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c, i) => (
                      <tr key={i} className={i === 0 ? "cd-row-top" : ""}>
                        <td><RankBadge rank={c.rank} /></td>
                        <td>
                          <div className="cd-cand-name">
                            <div className="cd-cand-avatar">{c.candidate_ref?.slice(-3)}</div>
                            <code className="cd-cand-ref">{c.candidate_ref}</code>
                          </div>
                        </td>
                        <td><ScoreBar value={c.scores?.technical ?? 0} /></td>
                        <td><ScoreBar value={c.scores?.communication ?? 0} /></td>
                        <td><ScoreBar value={c.scores?.confidence ?? 0} /></td>
                        <td>
                          <span className={`cd-overall-cell ${scoreClass(c.scores?.overall ?? 0)}`}>
                            {c.scores?.overall ?? "—"}
                          </span>
                        </td>
                        <td>
                          <IntegrityRing
                            score={c.integrity?.score ?? 0}
                            level={c.integrity?.level ?? "medium"}
                            flags={c.integrity?.flags || []}
                          />
                        </td>
                        <td>
                          <button className="cd-action-btn" title={`Report · ${c.candidate_ref}`}>
                            <IReport /> Report
                          </button>
                        </td>
                        <td>
                          <button className="cd-action-btn cd-action-rec" title={`Recording · ${c.candidate_ref}`}>
                            <span className="cd-play-dot"><IPlay /></span>
                            {String(8 + (c.rank * 7 % 18)).padStart(2, "0")}:{String((c.rank * 23 % 60)).padStart(2, "0")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ── Loading a specific job's results ── */
  if (jobLoading) {
    return (
      <div className="cd-page">
        <div className="cd-loading">
          <div className="cd-spinner" />
          <p>Loading results…</p>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════
     JOBS LIST VIEW
     ══════════════════════════════════════ */
  const totalCandidates  = jobs.reduce((s, j) => s + (j.total_candidates || 0), 0);
  const activeJobs       = jobs.filter(j => (j.status || "").toUpperCase() === "ACTIVE").length;
  const jobsWithScore    = jobs.filter(j => j.avg_score || j.average_score);
  const avgScore         = jobsWithScore.length
    ? (jobsWithScore.reduce((s, j) => s + (j.avg_score || j.average_score || 0), 0) / jobsWithScore.length).toFixed(1)
    : "—";

  const filterOptions = [
    { key: "all",     label: `All · ${jobs.length}` },
    { key: "active",  label: `Active · ${activeJobs}` },
    { key: "expired", label: `Expired · ${jobs.filter(j => j.status?.toUpperCase() === "EXPIRED").length}` },
  ];

  const filteredJobs = activeFilter === "all" ? jobs
    : jobs.filter(j => (j.status || "").toLowerCase() === activeFilter);

  return (
    <div className="cd-page">
      {/* ── Page header ── */}
      <div className="cd-page-head">
        <div>
          <h1>Hiring Dashboard</h1>
          <p className="cd-page-sub">{activeJobs} active opening{activeJobs !== 1 ? "s" : ""} · {totalCandidates} candidates evaluated</p>
        </div>
        <div className="cd-head-actions">
          <button className="cd-btn cd-btn-ghost"><IDownload /> Export</button>
          <button className="cd-btn cd-btn-ghost"><IFilter /> Filters</button>
          <a href="/company/jobs" className="cd-btn cd-btn-primary"><IPlus /> New job</a>
        </div>
      </div>

      {/* ── KPI row ── */}
      <div className="cd-kpi-row">
        <KpiCard label="Active jobs"            value={activeJobs}       sub="open positions"     icon={<IJob />} />
        <KpiCard label="Candidates evaluated"   value={totalCandidates}  sub="total across all jobs" icon={<ICand />} />
        <KpiCard label="Avg. overall score"     value={avgScore}         sub="composite score"    icon={<IStar />} />
        <KpiCard label="Integrity flags"        value="—"                sub="across all jobs"    icon={<IWarn />} accent />
      </div>

      {jobs.length === 0 ? (
        <div className="cd-empty">
          <IJob />
          <h3>No jobs yet</h3>
          <p>Create your first job opening to start receiving candidates.</p>
          <a href="/company/jobs" className="cd-btn cd-btn-primary"><IPlus /> Create first job</a>
        </div>
      ) : (
        <div className="cd-split cd-split-main">
          {/* ── Jobs table ── */}
          <div className="cd-panel cd-jobs-panel">
            <div className="cd-panel-head">
              <div>
                <h2>Open positions</h2>
                <p className="cd-panel-sub">Click a row to view candidate rankings</p>
              </div>
              <div className="cd-tabs">
                {filterOptions.map(f => (
                  <button key={f.key} className={activeFilter === f.key ? "on" : ""}
                    onClick={() => setActiveFilter(f.key)}>{f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="cd-table-scroll">
              <table className="cd-jobs-table">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Status</th>
                    <th>Candidates</th>
                    <th>Avg. score</th>
                    <th>Closes</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map(job => {
                    const pct = Math.min(100, (job.total_candidates || 0) * 2);
                    const avg = job.avg_score || job.average_score;
                    return (
                      <tr key={job.job_id}
                        className="cd-job-row clickable"
                        onClick={() => fetchJobResults(job.job_id)}
                      >
                        <td>
                          <div className="cd-job-title">
                            <div className="cd-job-icon"><IJob /></div>
                            <div>
                              <div className="cd-job-name">
                                {job.job_name}
                                <EventTypeBadge eventType={job.event_type} includeCoding={!!job.config?.include_coding} />
                              </div>
                              <div className="cd-job-dept">{job.department || job.location || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td><StatusPill status={job.status} /></td>
                        <td>
                          <div className="cd-cand-cell">
                            <div className="cd-cand-bar"><span style={{ width: `${pct}%` }} /></div>
                            <span className="cd-cand-num">{job.total_candidates || 0}</span>
                          </div>
                        </td>
                        <td>
                          {avg
                            ? <span className="cd-score-chip">{Number(avg).toFixed(1)}</span>
                            : <span className="cd-muted">No data</span>}
                        </td>
                        <td><span className="cd-muted">{fmtDate(job.valid_till)}</span></td>
                        <td>
                          <div className="cd-row-actions" onClick={e => e.stopPropagation()}>
                            <button className="cd-icon-btn" title="View results"
                              disabled={jobLoading}
                              onClick={() => fetchJobResults(job.job_id)}>
                              <IArrow />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Right column: Pipeline ── */}
          <div className="cd-right-col">
            <div className="cd-panel">
              <div className="cd-panel-head">
                <div>
                  <h2>Candidate pipeline</h2>
                  <p className="cd-panel-sub">All openings</p>
                </div>
              </div>
              <div className="cd-pipeline">
                <PipelineRow label="Jobs posted"        value={jobs.length}     max={jobs.length}   color="#667eea" />
                <PipelineRow label="With candidates"    value={jobs.filter(j => j.total_candidates > 0).length} max={jobs.length} color="#764ba2" />
                <PipelineRow label="Total candidates"   value={totalCandidates} max={Math.max(totalCandidates, 1)} color="#5b3a87" />
                <PipelineRow label="Active jobs"        value={activeJobs}      max={jobs.length}   color="#36d399" />
              </div>
            </div>

            <div className="cd-panel">
              <div className="cd-panel-head">
                <div>
                  <h2>Quick actions</h2>
                </div>
              </div>
              <div className="cd-quick-actions">
                <a href="/company/jobs" className="cd-quick-btn">
                  <IPlus /> Post new job
                </a>
                <button className="cd-quick-btn" onClick={fetchJobs}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 12a9 9 0 1018 0M21 4v6h-6"/></svg>
                  Refresh data
                </button>
              </div>
              {error && <p className="cd-error-inline">{error}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyDashboard;
